import Reflux from "reflux";
import { ArchiveBackends, ArchiveClusterArchives } from "@graylog/enterprise-api";
import i18n from "i18n";
import ArchiveActions from "archive/ArchiveActions";
import UserNotification from "util/UserNotification";
import { qualifyUrl } from "util/URLUtils";
import fetch, { fetchPeriodically } from "logic/rest/FetchProvider";
export const FILESYSTEM_TYPE = "fs-1";
export const S3_TYPE = "s3-1";
export const GCS_TYPE = "gcs-1";
export const ABS_TYPE = "abs-1";
export const BACKEND_TYPES = {
  [FILESYSTEM_TYPE]: "File system",
  [S3_TYPE]: "S3",
  [GCS_TYPE]: "GCS",
  [ABS_TYPE]: "Azure Blob"
};
const urlPrefix = "/plugins/org.graylog.plugins.archive";
const ArchiveStore = Reflux.createStore({
  listenables: [ArchiveActions],
  diskStates: {},
  filenamesExport: void 0,
  getInitialState() {
    return this.getState();
  },
  getState() {
    return {
      diskStates: this.diskStates,
      filenamesExport: this.filenamesExport
    };
  },
  _propagateUpdate() {
    return this.trigger(this.getState());
  },
  _errorHandler(message, title, cb) {
    return (error) => {
      let errorMessage;
      try {
        errorMessage = error.additional.body.message;
      } catch (_) {
        errorMessage = error.message;
      }
      UserNotification.error(`${message}: ${errorMessage}`, title);
      if (cb) {
        cb(error);
      }
    };
  },
  _archivesUrl(path) {
    return qualifyUrl(`${urlPrefix}/cluster/archives${path}`);
  },
  _formDataForBulkRestore(archives) {
    const bulkRestoreData = {};
    archives.forEach((archive) => {
      if (bulkRestoreData[archive.backend_id] === void 0) {
        bulkRestoreData[archive.backend_id] = [];
      }
      bulkRestoreData[archive.backend_id].push(archive.archive_id);
    });
    return bulkRestoreData;
  },
  searchPaginated(page, perPage, query, isPeriodicalRequest = false) {
    let url;
    const fetchMethod = isPeriodicalRequest ? fetchPeriodically : fetch;
    if (query) {
      url = this._archivesUrl(`/catalog?page=${page}&per_page=${perPage}&query=${encodeURIComponent(query)}`);
    } else {
      url = this._archivesUrl(`/catalog?page=${page}&per_page=${perPage}`);
    }
    const promise = fetchMethod("GET", url);
    promise.then(
      (response) => {
        const pagination = {
          count: response.count,
          total: response.total,
          page: response.page,
          per_page: response.per_page,
          query: response.query
        };
        this.catalog = {
          archives: response.archives,
          archivesContext: response.archives_context,
          backendsContext: response.backends_context,
          pagination
        };
        this._propagateUpdate();
      },
      this._errorHandler(
        i18n.t("notifications.fetch_catalog_failed", { ns: "enterprise_archive" }),
        i18n.t("notifications.fetch_catalog_failed_title", { ns: "enterprise_archive" })
      )
    );
    ArchiveActions.searchPaginated.promise(promise);
  },
  createArchive(indexName) {
    const promise = ArchiveClusterArchives.run(indexName);
    promise.then(
      () => {
        UserNotification.success(
          i18n.t("notifications.create_started", { ns: "enterprise_archive", index_name: indexName })
        );
      },
      this._errorHandler(
        i18n.t("notifications.create_failed", { ns: "enterprise_archive" }),
        i18n.t("notifications.create_failed_title", { ns: "enterprise_archive", index_name: indexName })
      )
    );
    ArchiveActions.createArchive.promise(promise);
  },
  restoreArchive(backendId, archiveId) {
    const promise = ArchiveClusterArchives.restoreFromBackend(backendId, archiveId);
    promise.then(
      () => {
        UserNotification.success(
          i18n.t("notifications.restore_started", { ns: "enterprise_archive", archive_id: archiveId })
        );
      },
      this._errorHandler(
        i18n.t("notifications.restore_failed", { ns: "enterprise_archive" }),
        i18n.t("notifications.restore_failed_title", { ns: "enterprise_archive", archive_id: archiveId })
      )
    );
    ArchiveActions.restoreArchive.promise(promise);
  },
  restoreArchives(archivesToRestore) {
    const bulkRestoreData = this._formDataForBulkRestore(archivesToRestore);
    const promise = ArchiveClusterArchives.bulkRestoreFromBackend({ archives: bulkRestoreData });
    promise.then(
      () => {
        UserNotification.success(
          i18n.t("notifications.restore_bulk_started", {
            ns: "enterprise_archive",
            archives: JSON.stringify(bulkRestoreData)
          })
        );
      },
      this._errorHandler(
        i18n.t("notifications.restore_failed", { ns: "enterprise_archive" }),
        i18n.t("notifications.restore_bulk_failed_title", {
          ns: "enterprise_archive",
          archives: JSON.stringify(bulkRestoreData)
        })
      )
    );
    ArchiveActions.restoreArchives.promise(promise);
  },
  deleteArchive(backendId, archiveId) {
    const promise = ArchiveClusterArchives.deleteByBackend(backendId, archiveId);
    promise.then(
      () => {
        UserNotification.success(
          i18n.t("notifications.delete_success", { ns: "enterprise_archive", archive_id: archiveId })
        );
      },
      this._errorHandler(
        i18n.t("notifications.delete_failed", { ns: "enterprise_archive" }),
        i18n.t("notifications.delete_failed_title", { ns: "enterprise_archive", archive_id: archiveId })
      )
    );
    ArchiveActions.deleteArchive.promise(promise);
  },
  availability(entryId) {
    const promise = fetch("GET", this._archivesUrl(`/catalog/${entryId}/available`));
    this.diskStates[entryId] = { loading: true };
    this._propagateUpdate();
    promise.then(
      (response) => {
        this.diskStates[entryId] = { available: response.available, loading: false };
        this._propagateUpdate();
      },
      this._errorHandler(
        i18n.t("notifications.availability_failed", { ns: "enterprise_archive" }),
        i18n.t("notifications.availability_failed_title", { ns: "enterprise_archive", archive_id: entryId })
      )
    );
    ArchiveActions.availability.promise(promise);
  },
  rebuildCatalog() {
    const promise = ArchiveClusterArchives.rebuild();
    promise.then(() => {
      UserNotification.success(i18n.t("notifications.rebuild_catalog_success", { ns: "enterprise_archive" }));
    });
    ArchiveActions.rebuildCatalog.promise(promise);
  },
  checkOutputTemplate(template) {
    const promise = ArchiveBackends.testOutputTemplate(template || "");
    ArchiveActions.checkOutputTemplate.promise(promise);
  },
  exportFilenames(query) {
    let url = this._archivesUrl("/catalog/export/filename");
    if (query) {
      url = `${url}?query=${encodeURIComponent(query)}`;
    }
    const promise = fetch("GET", url);
    promise.then(
      (response) => {
        this.filenameExport = response.filenames;
        this._propagateUpdate();
      },
      this._errorHandler(
        i18n.t("notifications.export_filenames_failed", { ns: "enterprise_archive" }),
        i18n.t("notifications.export_filenames_failed_title", { ns: "enterprise_archive" })
      )
    );
    ArchiveActions.exportFilenames.promise(promise);
  }
});
export default ArchiveStore;
